/**
 * Formats an employee's full name for display by dropping middle name(s).
 * The backend stores/returns names as a single composed string
 * (e.g. "Firstname Middlename Lastname") with no separate name-part fields,
 * so middle name(s) are stripped heuristically: keep the first and last
 * whitespace-separated tokens, drop everything in between.
 *
 * Labels that append an employee code in parentheses (e.g.
 * "Sachin Dnyandeo Wagh (NIPL1169)") keep that suffix; middle-name stripping
 * applies only to the name portion so the ID is not treated as a surname.
 *
 * @param {string} name - full name as received from the API
 * @returns {string} name with middle name(s) removed
 */
export function formatEmployeeName(name) {
  if (!name || typeof name !== 'string') {
    return name || '';
  }

  const trimmed = name.trim();
  if (!trimmed) {
    return '';
  }

  // Preserve trailing "(EMPID)" so it is not treated as the last name token.
  const idMatch = trimmed.match(/^(.*?)\s*(\([A-Za-z0-9_-]+\))\s*$/);
  const namePart = idMatch ? idMatch[1].trim() : trimmed;
  const idSuffix = idMatch ? ` ${idMatch[2]}` : '';

  if (!namePart) {
    return idMatch ? idMatch[2] : '';
  }

  const parts = namePart.split(/\s+/).filter(Boolean);

  if (parts.length <= 2) {
    return `${parts.join(' ')}${idSuffix}`.trim();
  }

  return `${parts[0]} ${parts[parts.length - 1]}${idSuffix}`.trim();
}

export default formatEmployeeName;
